import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Radar, Link2, Plus, Loader2, ChevronRight, Zap, Trophy, RefreshCw, ScrollText, LineChart, Network, Crosshair, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, Modal, Field, GoldButton, GhostButton, inputCls,
  ScoreBadge, StageBadge, PlatformTag, PLATFORM_META, EmptyState,
} from "@/components/app/widgets";

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [discovering, setDiscovering] = useState("");
  const [limits, setLimits] = useState({});
  const [clearing, setClearing] = useState(false);
  const [discoverQuery, setDiscoverQuery] = useState("");
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ platform: "podcast", name: "", url: "", creator: "", description: "", audience_size: "", contact_info: "" });
  const [analysis, setAnalysis] = useState(null);
  const [topOpps, setTopOpps] = useState([]);
  const [perf, setPerf] = useState(null);
  const [brief, setBrief] = useState(null);
  const [stratBusy, setStratBusy] = useState("");
  const [dist, setDist] = useState(null);

  const loadDist = () => api.get(`/campaigns/${campaignId}/distribution-dashboard`).then((r) => setDist(r.data)).catch(() => {});

  const genPlan = async () => {
    setStratBusy("plan");
    try {
      const r = await api.post(`/campaigns/${campaignId}/distribution-plan`);
      setDist((d) => ({ ...(d || {}), distribution_plan: r.data.distribution_plan }));
      toast.success("Unified distribution plan generated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setStratBusy("");
    }
  };

  const genFocus = async () => {
    setStratBusy("focus");
    try {
      const r = await api.post(`/campaigns/${campaignId}/focus`);
      setDist((d) => ({ ...(d || {}), focus: r.data.focus }));
      toast.success("Focus recommendation ready");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setStratBusy("");
    }
  };

  const loadPerf = () => api.get(`/campaigns/${campaignId}/performance`).then((r) => setPerf(r.data)).catch(() => {});

  const genBrief = async () => {
    setStratBusy("brief");
    try {
      const r = await api.post(`/campaigns/${campaignId}/strategy-brief`);
      setBrief(r.data.strategy_brief);
      toast.success("Campaign strategy brief generated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setStratBusy("");
    }
  };

  const interpret = async () => {
    setStratBusy("interpret");
    try {
      const r = await api.post(`/campaigns/${campaignId}/performance/interpret`);
      setPerf((p) => ({ ...p, interpretation: r.data.interpretation, metrics: r.data.metrics }));
      toast.success("Performance interpretation ready");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setStratBusy("");
    }
  };

  useEffect(() => {
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/campaigns/${campaignId}/analyze/status`);
        if (cancelled) return;
        setAnalysis(r.data);
        if (r.data.job?.status === "running") {
          timer = setTimeout(poll, 3000);
        } else {
          load();
        }
      } catch {}
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [campaignId]);

  useEffect(() => {
    api.get(`/campaigns/${campaignId}/top-opportunities`).then((r) => setTopOpps(r.data.items)).catch(() => {});
  }, [campaignId, analysis?.job?.status]);

  const analyze = async (retry = false) => {
    try {
      await api.post(`/campaigns/${campaignId}/analyze`, { retry_failed: retry });
      toast.success(retry ? "Retrying failed prospects" : "Campaign analysis started — runs in the background");
      const r = await api.get(`/campaigns/${campaignId}/analyze/status`);
      setAnalysis(r.data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const load = async () => {
    try {
      const [c, p, i] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/prospects?campaign_id=${campaignId}`),
        api.get("/discovery/integrations"),
      ]);
      setCampaign(c.data);
      setProspects(p.data.items);
      setIntegrations(i.data.integrations);
      setBrief(c.data.strategy_brief || null);
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  useEffect(() => { load(); loadPerf(); loadDist(); }, [campaignId]);

  const runDiscovery = async (platform) => {
    setDiscovering(platform);
    try {
      const res = await api.post(`/campaigns/${campaignId}/discover`, {
        platform,
        limit: limits[platform] || 15,
        query: discoverQuery.trim() || undefined,
      });
      const status = res.data.provider_status;
      if (res.data.created === 0 && res.data.items.length === 0) {
        toast.info(status.reason || `No new results from ${status.source}. Try different keywords or URL import.`);
      } else {
        toast.success(`Discovered ${res.data.created} new prospects (${res.data.skipped_duplicates} duplicates skipped)`);
      }
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setDiscovering("");
    }
  };

  const doImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      await api.post("/prospects/import-url", { url: importUrl.trim(), campaign_id: campaignId });
      toast.success("Prospect imported — research it to build intelligence");
      setImportUrl("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImporting(false);
    }
  };

  const saveManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prospects", { ...manual, campaign_id: campaignId });
      toast.success("Prospect added");
      setManualOpen(false);
      setManual({ platform: "podcast", name: "", url: "", creator: "", description: "", audience_size: "", contact_info: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!campaign) return <Spinner label="Loading campaign" />;

  return (
    <div data-testid="campaign-detail-page">
      <PageHeader title={campaign.name} sub={`${campaign.product_name} · ${campaign.objective} · ${campaign.geography} · ${campaign.language}`}>
        <GhostButton
          onClick={async () => {
            const n = campaign.prospect_count ?? "";
            if (!window.confirm(`Delete ALL ${n ? n + " " : ""}prospects from this campaign? This removes them from every count and queue. Only this campaign is affected.`)) return;
            setClearing(true);
            try {
              const r = await api.delete(`/campaigns/${campaignId}/prospects`);
              toast.success(`Removed ${r.data.deleted_prospects} prospects from this campaign`);
              load(); loadPerf(); loadDist();
            } catch (err) {
              toast.error(apiError(err));
            } finally {
              setClearing(false);
            }
          }}
          disabled={clearing}
          data-testid="clear-prospects-button"
        >
          {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          Clear Prospects
        </GhostButton>
        <GhostButton onClick={() => setManualOpen(true)} data-testid="add-manual-prospect-button">
          <Plus className="h-4 w-4" /> Add Manually
        </GhostButton>
      </PageHeader>

      {perf && (
        <section className="mb-6 border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="campaign-performance">
          <div className="flex flex-wrap items-center gap-3">
            <LineChart className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">Campaign Performance</h2>
            <span className="text-[10px] uppercase tracking-wider text-zinc-600">observed metrics · demo excluded</span>
            <button
              onClick={interpret} disabled={!!stratBusy}
              data-testid="interpret-performance-button"
              className="ml-auto flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-50"
            >
              {stratBusy === "interpret" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              AI Interpretation
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
            {[
              ["discovered", perf.metrics.discovered, perf.targets?.prospects],
              ["researched", perf.metrics.researched],
              ["qualified", perf.metrics.qualified],
              ["high-priority", perf.metrics.high_priority],
              ["drafts approved", perf.metrics.drafts_approved],
              ["responses", perf.metrics.responses],
              ["partnerships", perf.metrics.partnerships],
              ["leads", perf.metrics.leads, perf.targets?.leads],
              ["customers", perf.metrics.customers, perf.targets?.customers],
              ["revenue", `$${perf.metrics.revenue.toLocaleString()}`, perf.targets?.revenue ? `$${Number(perf.targets.revenue).toLocaleString()}` : null],
            ].map(([label, val, target]) => (
              <span key={label}>
                <span className="font-serif text-lg font-semibold text-white">{val}</span>
                {target ? <span className="text-zinc-600">/{target}</span> : null}{" "}
                <span className="text-xs">{label}</span>
              </span>
            ))}
          </div>
          {perf.interpretation && (
            <div className="mt-5 border-t border-white/10 pt-4" data-testid="performance-interpretation">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                AI interpretation · data sufficiency: <span className="text-zinc-300">{perf.interpretation.data_sufficiency}</span>
              </p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-300">{perf.interpretation.summary}</p>
              {perf.interpretation.recommendations?.length > 0 && (
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-zinc-400">
                  {perf.interpretation.recommendations.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      <section className="mb-6 border border-[#27272A] bg-[#0A0A0A] p-6" data-testid="strategy-brief-section">
        <div className="flex flex-wrap items-center gap-3">
          <ScrollText className="h-4 w-4 text-[#D4AF37]" />
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">Campaign Strategy Brief</h2>
          <button
            onClick={genBrief} disabled={!!stratBusy}
            data-testid="generate-brief-button"
            className="ml-auto flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {stratBusy === "brief" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            {brief ? "Regenerate Brief" : "Generate Brief"}
          </button>
        </div>
        {brief ? (
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Objective</p><p className="mt-1 text-sm text-zinc-200">{brief.campaign_objective}</p></div>
              <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Ideal partner profile</p><p className="mt-1 text-sm text-zinc-200">{brief.ideal_partner_profile}</p></div>
              <div><p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Value-first strategy</p><p className="mt-1 text-sm text-zinc-200">{brief.value_first_strategy}</p></div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Best platform mix</p>
                <ul className="mt-1 space-y-1 text-sm text-zinc-300">
                  {(brief.best_platform_mix || []).map((x, i) => <li key={i}>· <span className="font-semibold capitalize">{x.platform}</span> — {x.why}</li>)}
                </ul>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Top opportunities</p>
                <ul className="mt-1 space-y-1.5 text-sm">
                  {(brief.top_opportunities || []).map((t, i) => (
                    <li key={i}>
                      {t.prospect_id ? (
                        <Link to={`/app/prospects/${t.prospect_id}`} className="text-[#D4AF37] hover:brightness-110">{t.name}</Link>
                      ) : <span className="text-zinc-300">{t.name}</span>}
                      <span className="text-zinc-500"> — {t.why}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Key risks</p>
                <ul className="mt-1 list-inside list-disc space-y-1 text-sm text-zinc-400">
                  {(brief.key_risks || []).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Next actions</p>
                <ol className="mt-1 list-inside list-decimal space-y-1 text-sm text-zinc-300">
                  {(brief.next_actions || []).map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-zinc-500">
            Generate an AI strategy brief: objective, platform mix, ideal partner profile, value-first strategy, top opportunities, conversion path, risks and next actions.
          </p>
        )}
      </section>

      {/* Distribution Orchestrator */}
      <section className="mb-6 border border-[#D4AF37]/30 bg-[#0A0A0A] p-6" data-testid="distribution-orchestrator">
        <div className="flex flex-wrap items-center gap-3">
          <Network className="h-4 w-4 text-[#D4AF37]" />
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">Distribution Orchestrator</h2>
          <span className="text-[10px] uppercase tracking-wider text-zinc-600">one campaign · multiple platforms · one central strategy</span>
          <div className="ml-auto flex gap-2">
            <button
              onClick={genPlan} disabled={!!stratBusy}
              data-testid="distribution-plan-button"
              className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
            >
              {stratBusy === "plan" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              {dist?.distribution_plan ? "Regenerate Plan" : "Distribution Plan"}
            </button>
            <button
              onClick={genFocus} disabled={!!stratBusy}
              data-testid="focus-recommendation-button"
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-50"
            >
              {stratBusy === "focus" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Crosshair className="h-3 w-3" />}
              Where Should I Focus?
            </button>
          </div>
        </div>

        {dist?.focus && (
          <div className="mt-5 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5" data-testid="focus-panel">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
              Focus: <span className="capitalize">{dist.focus.primary_focus}</span>
              <span className="ml-2 text-zinc-500">data sufficiency: {dist.focus.data_sufficiency}</span>
            </p>
            <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-zinc-300">
              {(dist.focus.why || []).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            {dist.focus.cautions?.length > 0 && (
              <p className="mt-2 text-xs italic text-amber-400">{dist.focus.cautions[0]}</p>
            )}
          </div>
        )}

        {dist?.platforms && Object.keys(dist.platforms).length > 0 && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 text-[9px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                  <th className="py-2 pr-4">Platform</th><th className="py-2 pr-4">Prospects</th>
                  <th className="py-2 pr-4">Researched</th><th className="py-2 pr-4">High-priority</th>
                  <th className="py-2 pr-4">Avg score</th><th className="py-2 pr-4">Avg match</th>
                  <th className="py-2 pr-4">Partnerships</th><th className="py-2 pr-4">Leads</th>
                  <th className="py-2 pr-4">Customers</th><th className="py-2">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {Object.entries(dist.platforms).map(([pl, s]) => (
                  <tr key={pl} data-testid={`dist-row-${pl}`}>
                    <td className="py-2.5 pr-4"><PlatformTag platform={pl} /></td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.prospects}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.research_completion}%</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.high_priority}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.avg_score ?? "—"}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.avg_match ?? "—"}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.partnerships}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.leads}</td>
                    <td className="py-2.5 pr-4 text-zinc-300">{s.customers}</td>
                    <td className="py-2.5 font-semibold text-[#D4AF37]">${s.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {dist?.distribution_plan && (
          <div className="mt-5 grid gap-4 lg:grid-cols-2 xl:grid-cols-3" data-testid="distribution-plan">
            <div className="border border-white/10 p-5 lg:col-span-2 xl:col-span-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Central strategy</p>
              <p className="mt-1.5 text-sm leading-relaxed text-zinc-200">{dist.distribution_plan.central_strategy}</p>
              {dist.distribution_plan.sequencing && (
                <p className="mt-2 text-xs text-zinc-500">{dist.distribution_plan.sequencing}</p>
              )}
            </div>
            {(dist.distribution_plan.platform_strategies || []).map((ps) => (
              <div key={ps.platform} className="border border-white/10 p-5" data-testid={`plan-${ps.platform}`}>
                <div className="flex items-center justify-between gap-3">
                  <PlatformTag platform={ps.platform} />
                  <span className="line-clamp-2 text-right text-[9px] uppercase tracking-wider text-zinc-400">{ps.engine_status_note}</span>
                </div>
                <p className="mt-2 text-sm font-semibold text-white">{ps.role}</p>
                <div className="mt-3 space-y-2 text-xs leading-relaxed text-zinc-400">
                  <p><span className="text-zinc-300">Discovery:</span> {ps.discovery_approach}</p>
                  <p><span className="text-zinc-300">Value-first:</span> {ps.value_first_angle}</p>
                  {ps.partnership_formats?.length > 0 && (
                    <p><span className="text-zinc-300">Formats:</span> {ps.partnership_formats.join(", ")}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="border border-[#27272A] bg-[#0A0A0A] p-6">
        <div className="flex flex-wrap items-center gap-3">
          <Radar className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300">Prospect Discovery</h2>
          <button
            onClick={() => analyze(false)}
            disabled={analysis?.job?.status === "running"}
            data-testid="analyze-campaign-button"
            className="ml-auto flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
          >
            {analysis?.job?.status === "running" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            Analyze Campaign
          </button>
          {campaign.channels.includes("podcast") && (
            <button
              onClick={async () => {
                setDiscovering("batch");
                try {
                  const res = await api.post(`/campaigns/${campaignId}/deep-research-all?limit=25`);
                  toast.success(`Deep research complete for ${res.data.researched} podcast prospects${res.data.failed.length ? ` (${res.data.failed.length} failed)` : ""}`);
                  load();
                } catch (err) {
                  toast.error(apiError(err));
                } finally {
                  setDiscovering("");
                }
              }}
              disabled={!!discovering}
              data-testid="deep-research-all-button"
              className="flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] transition-colors hover:bg-[#D4AF37]/10 disabled:opacity-50"
            >
              {discovering === "batch" ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Deep Research All Podcasts
            </button>
          )}
        </div>

        {analysis?.job && (
          <div data-testid="analysis-progress" className="mt-4 border border-white/10 bg-[#050505] p-4">
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <span className="font-semibold text-white">
                {analysis.job.done} / {analysis.job.total} analyzed
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                analysis.job.status === "running" ? "bg-[#D4AF37]/10 text-[#D4AF37]"
                : analysis.job.status === "complete" ? "bg-emerald-500/10 text-emerald-400"
                : "bg-white/5 text-zinc-400"
              }`}>
                {analysis.job.status}
              </span>
              <span className="text-zinc-500">
                {analysis.counts.ANALYZED} analyzed · {analysis.counts.PROCESSING} processing ·{" "}
                {analysis.counts.NOT_ANALYZED} queued ·{" "}
                <span className={analysis.counts.FAILED ? "text-red-400" : ""}>{analysis.counts.FAILED} failed</span>
              </span>              {analysis.job.status !== "running" && analysis.counts.FAILED > 0 && (
                <button
                  onClick={() => analyze(true)}
                  data-testid="retry-failed-button"
                  className="ml-auto flex items-center gap-1 rounded-full border border-amber-500/50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-400 hover:bg-amber-500/10"
                >
                  <RefreshCw className="h-3 w-3" /> Retry failed only
                </button>
              )}
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-[#D4AF37] transition-[width] duration-500"
                style={{ width: `${analysis.job.total ? (analysis.job.done / analysis.job.total) * 100 : 0}%` }}
              />
            </div>
            {analysis.by_platform && Object.keys(analysis.by_platform).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-zinc-500">
                {Object.entries(analysis.by_platform).map(([pl, s]) => (
                  <span key={pl}>
                    <span className="font-semibold capitalize text-zinc-300">{pl}</span>: {s.analyzed}/{s.total} analyzed
                    {s.failed > 0 && <span className="text-red-400"> · {s.failed} failed</span>}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mt-5">
          <input
            type="text"
            value={discoverQuery}
            onChange={(e) => setDiscoverQuery(e.target.value)}
            placeholder="Discovery query (optional) — e.g. 'apprendre le français' — applies to the engine you run; blank uses campaign keywords"
            data-testid="discover-query-input"
            className="w-full rounded-md border border-[#27272A] bg-[#050505] px-3 py-2 text-xs text-white outline-none placeholder:text-zinc-600 focus:border-[#D4AF37]"
          />
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {campaign.channels.map((ch) => {
            const integ = integrations?.[ch] || {};
            const live = integ.status === "live";
            return (
              <div key={ch} data-testid={`discover-panel-${ch}`} className="border border-white/10 p-4">
                <div className="flex items-center justify-between">
                  <PlatformTag platform={ch} />
                  <span
                    data-testid={`integration-status-${ch}`}
                    className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                      ch === "podcast"
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-amber-500/10 text-amber-400"
                    }`}
                  >
                    {ch === "podcast" ? "Live · Apple catalog" : "Web search · official API pending"}
                  </span>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-zinc-400">{integ.note}</p>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    type="number" min={1} max={1000} value={limits[ch] || 15}
                    onChange={(e) => setLimits({ ...limits, [ch]: Number(e.target.value) })}
                    data-testid={`discover-limit-${ch}`}
                    title="No arbitrary cap — bounded only by source availability, provider technical ceilings and rate limits"
                    className="w-20 rounded-md border border-[#27272A] bg-[#050505] px-2 py-1.5 text-xs text-white outline-none focus:border-[#D4AF37]"
                  />
                  <GoldButton onClick={() => runDiscovery(ch)} disabled={!!discovering} data-testid={`discover-run-${ch}`} className="px-4 py-1.5">
                    {discovering === ch ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Discover"}
                  </GoldButton>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={doImport} className="mt-5 flex flex-wrap items-center gap-3 border-t border-white/5 pt-5">
          <Link2 className="h-4 w-4 text-zinc-500" />
          <input
            type="text" value={importUrl} onChange={(e) => setImportUrl(e.target.value)}
            placeholder="Paste any public prospect URL (YouTube channel, FB group, TikTok/IG profile, podcast page)…"
            data-testid="import-url-input"
            className="min-w-0 flex-1 rounded-md border border-[#27272A] bg-[#050505] px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-[#D4AF37]"
          />
          <GoldButton type="submit" disabled={importing} data-testid="import-url-button">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import & Research"}
          </GoldButton>
        </form>
      </div>

      {topOpps.length > 0 && (
        <section className="mt-8 border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-6" data-testid="top-opportunities">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
            <Trophy className="h-4 w-4" /> Top Partnership Opportunities
          </h2>
          <ul className="mt-4 divide-y divide-white/5">
            {topOpps.slice(0, 5).map((p, i) => (
              <li key={p.prospect_id}>
                <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`top-opp-${p.prospect_id}`} className="flex items-center gap-4 py-3 transition-colors hover:bg-white/[0.03]">
                  <span className="w-6 font-serif text-lg italic text-[#D4AF37]">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-white">{p.name}</span>
                  {p.has_blueprint && <span className="rounded-full border border-[#D4AF37]/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#D4AF37]">Blueprint</span>}
                  <PlatformTag platform={p.platform} />
                  <span className="font-serif text-lg font-semibold text-[#D4AF37]">{p.score?.total}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-8">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
          Prospects ({prospects.length})
        </h2>
        {prospects.length === 0 ? (
          <EmptyState
            icon={Radar}
            title="No prospects yet"
            desc="Run discovery above, import a URL, or add a prospect manually. No mock data — everything here is real."
          />
        ) : (
          <div className="overflow-x-auto border border-[#27272A]">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-[#0A0A0A] text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-5 py-3">Prospect</th>
                  <th className="px-5 py-3">Platform</th>
                  <th className="px-5 py-3">Score</th>
                  <th className="px-5 py-3">Stage</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {prospects.map((p) => (
                  <tr key={p.prospect_id} data-testid={`prospect-row-${p.prospect_id}`} className="transition-colors hover:bg-white/[0.03]">
                    <td className="max-w-[280px] px-5 py-3.5">
                      <p className="truncate font-medium text-white">{p.name}</p>
                      {p.creator && <p className="truncate text-xs text-zinc-500">{p.creator}</p>}
                    </td>
                    <td className="px-5 py-3.5"><PlatformTag platform={p.platform} /></td>
                    <td className="px-5 py-3.5"><ScoreBadge score={p.score?.total} /></td>
                    <td className="px-5 py-3.5"><StageBadge stage={p.status} /></td>
                    <td className="px-5 py-3.5 text-xs text-zinc-500">{p.source?.provider}</td>
                    <td className="px-5 py-3.5 text-right">
                      <Link to={`/app/prospects/${p.prospect_id}`} data-testid={`open-prospect-${p.prospect_id}`} className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:brightness-110">
                        Open <ChevronRight className="h-3.5 w-3.5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={manualOpen} onClose={() => setManualOpen(false)} title="Add Prospect Manually" wide>
        <form onSubmit={saveManual} data-testid="manual-prospect-form" className="grid gap-4 sm:grid-cols-2">
          <Field label="Platform">
            <select value={manual.platform} onChange={(e) => setManual({ ...manual, platform: e.target.value })} data-testid="manual-platform-select" className={inputCls}>
              {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Name">
            <input required type="text" value={manual.name} onChange={(e) => setManual({ ...manual, name: e.target.value })} data-testid="manual-name-input" className={inputCls} />
          </Field>
          <Field label="Profile / channel / group URL">
            <input type="text" value={manual.url} onChange={(e) => setManual({ ...manual, url: e.target.value })} data-testid="manual-url-input" className={inputCls} />
          </Field>
          <Field label="Creator / host / admin">
            <input type="text" value={manual.creator} onChange={(e) => setManual({ ...manual, creator: e.target.value })} data-testid="manual-creator-input" className={inputCls} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description">
              <textarea rows={2} value={manual.description} onChange={(e) => setManual({ ...manual, description: e.target.value })} data-testid="manual-description-input" className={inputCls} />
            </Field>
          </div>
          <Field label="Audience size (if known)">
            <input type="text" value={manual.audience_size} onChange={(e) => setManual({ ...manual, audience_size: e.target.value })} data-testid="manual-audience-input" className={inputCls} />
          </Field>
          <Field label="Public contact info">
            <input type="text" value={manual.contact_info} onChange={(e) => setManual({ ...manual, contact_info: e.target.value })} data-testid="manual-contact-input" className={inputCls} />
          </Field>
          <div className="flex justify-end gap-3 sm:col-span-2">
            <GhostButton type="button" onClick={() => setManualOpen(false)}>Cancel</GhostButton>
            <GoldButton type="submit" data-testid="manual-save-button">Add Prospect</GoldButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
